import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

interface SelectOption {
  value: string;
  label: string;
}

export function filterSelectOptions(options: SelectOption[], query: string): SelectOption[] {
  const search = query.trim().toLocaleLowerCase();
  return options.filter((option) => option.label.toLocaleLowerCase().includes(search));
}

interface SearchableSelectProps {
  id: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

/** A searchable, keyboard-accessible selector; typing does not change the view. */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id, value, options, onChange, placeholder = "Search…",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value);
  const filtered = useMemo(() => filterSelectOptions(options, query), [options, query]);
  const activeIndex = Math.min(highlighted, Math.max(0, filtered.length - 1));

  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const openMenu = () => {
    setQuery("");
    setHighlighted(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  };
  const choose = (nextValue: string) => {
    onChange(nextValue);
    close();
  };

  useEffect(() => {
    setOpen(false);
    setQuery("");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const outsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", outsideClick);
    return () => document.removeEventListener("mousedown", outsideClick);
  }, [open]);

  useEffect(() => {
    if (open) listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, query]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openMenu();
      } else if (filtered.length) {
        setHighlighted((activeIndex + (event.key === "ArrowDown" ? 1 : -1) + filtered.length) % filtered.length);
      }
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      if (filtered[activeIndex]) choose(filtered[activeIndex].value);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close();
    }
  };

  return (
    <div ref={rootRef} className="relative w-full min-w-0 sm:w-[420px]">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${id}-options`}
        aria-activedescendant={open && filtered.length ? `${id}-option-${activeIndex}` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={open ? query : selected?.label ?? ""}
        title={selected?.label}
        onFocus={openMenu}
        onClick={() => { if (!open) openMenu(); }}
        onBlur={(event) => {
          if (!rootRef.current?.contains(event.relatedTarget as Node | null)) close();
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlighted(0);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="w-full truncate rounded-xl border border-slate-200 bg-white py-2.5 pl-4 pr-10 text-sm text-slate-900 focus:border-softnu-500 focus:outline-none focus:ring-2 focus:ring-softnu-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={open ? "Close views" : "Search views"}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) close();
          else { inputRef.current?.focus(); openMenu(); }
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div ref={listRef} id={`${id}-options`} role="listbox" aria-label="Dashboard views" className="absolute left-0 right-0 z-30 mt-2 max-h-72 overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {filtered.length === 0 ? (
            <div role="status" className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">No matching views. Try another name or type.</div>
          ) : filtered.map((option, index) => (
            <button
              key={option.value}
              id={`${id}-option-${index}`}
              type="button"
              role="option"
              aria-selected={option.value === value}
              tabIndex={-1}
              title={option.label}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => choose(option.value)}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-slate-900 dark:text-slate-100 ${index === activeIndex ? "bg-softnu-50 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"}`}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {option.value === value && <Check className="h-4 w-4 shrink-0 text-softnu-600 dark:text-softnu-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
